import * as React from "react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";

import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/lib/shadcn/ui/alert-dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Label } from "@/lib/shadcn/ui/label";
import { DateInput } from "@/lib/shadcn/components/date-input";
import { cn } from "@/lib/shadcn/utils";
import { formatDateToISODate } from "@/utils/dates";
import { useDecommissionApartmentMutation } from "@/store/slices/apartments";

/** The minimal apartment shape this dialog needs. */
export interface DecommissionDialogApartment {
    id: number;
    name: string;
    /** The day the apartment went live; a removal date may not precede it (EC-005). */
    dateAdded: Date;
}

export interface DecommissionApartmentDialogProps {
    apartment: DecommissionDialogApartment;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Strip the time component so date-only comparisons are calendar-day exact. */
function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Guarded confirmation for decommissioning an apartment (AS-12, EC-005). Picks a
 * removal date (default today, any date allowed) and POSTs it to the decommission
 * mutation. The destructive cascade is described as generic warning text — no live
 * count/preview (UI-spec decision).
 */
export const DecommissionApartmentDialog: React.FC<DecommissionApartmentDialogProps> = ({
    apartment,
    open,
    onOpenChange,
}) => {
    const [removalDate, setRemovalDate] = useState<Date>(() => new Date());
    const [decommission, { isLoading }] = useDecommissionApartmentMutation();

    const errorId = useId();

    // Reset the picked date to today every time the dialog opens, so a previous
    // (possibly errored) attempt does not leak a stale date into a fresh open.
    useEffect(() => {
        if (open) {
            setRemovalDate(new Date());
        }
    }, [open]);

    const dateAdded = startOfDay(apartment.dateAdded);
    const isBeforeDateAdded = removalDate != null && startOfDay(removalDate) < dateAdded;
    const confirmDisabled = isLoading || removalDate == null || isBeforeDateAdded;

    const handleConfirm = async () => {
        // A plain Button (not AlertDialogAction) so Radix never auto-closes on click;
        // we control closing — only close on success, keep open (with the entered
        // date) on error.
        if (confirmDisabled) {
            return;
        }
        try {
            await decommission({ id: apartment.id, removalDate: formatDateToISODate(removalDate) }).unwrap();
            toast.success(`${apartment.name} decommissioned, effective ${formatDateToISODate(removalDate)}.`);
            onOpenChange(false);
        } catch {
            toast.error(`Failed to decommission ${apartment.name}. Please try again.`);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                data-testid="decommission-apartment-dialog"
                // While the mutation is in flight the dialog must not be dismissable.
                onEscapeKeyDown={(event) => {
                    if (isLoading) {
                        event.preventDefault();
                    }
                }}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>Decommission {apartment.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Blocks for this apartment that fall on or after this date will be trimmed or removed —
                        including blocks managed by HostHub. The apartment will drop off active lists, counts, and
                        the blocked-apartments page, and no new reservations, blocks, or imports dated after this
                        date will be accepted. Existing history before this date is unchanged. You can Reactivate
                        later, but trimmed or removed blocks are not restored.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-2">
                    <Label htmlFor="decommission-date-input">Removal date</Label>
                    <div data-testid="decommission-date">
                        <DateInput
                            id="decommission-date-input"
                            value={removalDate}
                            onChange={(date) => date && setRemovalDate(date)}
                            disabled={isLoading}
                            aria-describedby={isBeforeDateAdded ? errorId : undefined}
                            aria-invalid={isBeforeDateAdded}
                        />
                    </div>
                    {isBeforeDateAdded && (
                        <p id={errorId} role="alert" className="text-destructive text-sm">
                            Removal date can&apos;t be before the apartment went live on {formatDateToISODate(dateAdded)}.
                        </p>
                    )}
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
                    <Button
                        type="button"
                        data-testid="decommission-confirm"
                        className={cn("bg-red-600 text-white hover:bg-red-700")}
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                    >
                        {isLoading ? "Decommissioning…" : "Decommission"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default DecommissionApartmentDialog;
